import { useState, useEffect, useCallback } from 'react';
import { X, Smartphone } from 'lucide-react';
import { SITE_NAME } from '@/lib/siteConfig';

export default function InstallPWAButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    // Already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsStandalone(true);
      return;
    }
    // Previously dismissed
    try {
      if (sessionStorage.getItem('pwa_dismissed')) setDismissed(true);
    } catch {}

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') setDismissed(true);
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSHint(true);
    }
  }, [deferredPrompt, isIOS]);

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('pwa_dismissed', '1'); } catch {}
  };

  // Don't render if installed, dismissed, or not applicable
  if (isStandalone || dismissed) return null;
  if (!deferredPrompt && !isIOS) return null;

  return (
    <>
      {/* Floating button */}
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-1" data-testid="pwa-install-btn">
        <button
          onClick={handleInstall}
          className="flex items-center gap-1.5 px-3 py-2 bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold rounded-full shadow-lg shadow-sky-300/40 transition-all hover:scale-105 active:scale-95"
        >
          <Smartphone className="w-3.5 h-3.5" />
          На экран
        </button>
        <button
          onClick={handleDismiss}
          className="p-1 bg-white/90 hover:bg-white rounded-full shadow text-slate-400 hover:text-slate-600 transition-colors"
          data-testid="pwa-dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* iOS hint modal */}
      {showIOSHint && (
        <div className="fixed inset-0 z-[200] flex items-end justify-center p-4 bg-black/30" onClick={() => setShowIOSHint(false)}>
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full shadow-xl mb-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm text-slate-800">Установи {SITE_NAME}</h3>
              <button onClick={() => setShowIOSHint(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-xs text-slate-600">
              <div className="flex items-start gap-2">
                <span className="bg-sky-100 text-sky-700 font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-[10px]">1</span>
                <p>Нажми <span className="font-semibold">Поделиться</span> (иконка внизу экрана)</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-sky-100 text-sky-700 font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-[10px]">2</span>
                <p>Выбери <span className="font-semibold">На экран «Домой»</span></p>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-sky-100 text-sky-700 font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-[10px]">3</span>
                <p>Нажми <span className="font-semibold">Добавить</span></p>
              </div>
            </div>
            <button
              onClick={() => setShowIOSHint(false)}
              className="w-full mt-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold rounded-lg"
            >
              Понятно
            </button>
          </div>
        </div>
      )}
    </>
  );
}
